import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, Plane, Building, MapPin, Car, CalendarDays, ArrowLeft, FileDown, Loader2, UploadCloud } from 'lucide-react';
import VoucherForm from './VoucherForm';
import VoucherPreview from './VoucherPreview';
import BrandedVoucherLayout from '@/components/voucher/BrandedVoucherLayout';
import FlightVoucherBody from '@/components/voucher/FlightVoucherBody';
import HotelVoucherBody from '@/components/voucher/HotelVoucherBody';
import { ActivityVoucherBody, TransferVoucherBody, ItineraryVoucherBody } from '@/components/voucher/VoucherBodies';
import UploadRebrandDialog from '@/components/voucher/UploadRebrandDialog';
import { generatePDF } from '@/utils/generatePDF';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VOUCHER_ICONS = { flight: Plane, hotel: Building, activity: MapPin, transfer: Car, itinerary: CalendarDays };
const VOUCHER_LABELS = { flight: 'Flight', hotel: 'Hotel', activity: 'Activity', transfer: 'Transfer', itinerary: 'Itinerary' };
const STATUS_STYLES = { draft: 'bg-slate-100 text-slate-600', confirmed: 'bg-emerald-100 text-emerald-700' };

const Vouchers = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tripId = searchParams.get('tripId') || '';
  const [vouchers, setVouchers] = useState([]);
  const [trip, setTrip] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bundling, setBundling] = useState(false);
  const [view, setView] = useState('list'); // list | form | preview
  const [editingVoucher, setEditingVoucher] = useState(null);
  const [previewVoucher, setPreviewVoucher] = useState(null);
  const [creatingType, setCreatingType] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const bundleRef = useRef(null);

  const fetchVouchers = useCallback(async () => {
    try {
      const url = tripId ? `${BACKEND_URL}/api/vouchers?trip_id=${tripId}` : `${BACKEND_URL}/api/vouchers`;
      const res = await axios.get(url);
      setVouchers(res.data);
      if (tripId) {
        const tripRes = await axios.get(`${BACKEND_URL}/api/trips/${tripId}`);
        setTrip(tripRes.data);
      }
      const co = await axios.get(`${BACKEND_URL}/api/public/company`).catch(() => ({ data: null }));
      setCompany(co.data);
    } catch (err) { toast.error('Failed to load vouchers'); } finally { setLoading(false); }
  }, [tripId]);

  useEffect(() => { fetchVouchers(); }, [fetchVouchers]);

  const handleBundleExport = async () => {
    if (vouchers.length === 0) return;
    setBundling(true);
    try {
      const bundleUrl = `${window.location.origin}/bundle-preview/${tripId}`;

      const safeRef = String(tripId || 'bundle').replace(/[^a-zA-Z0-9-_]/g, '-');

      const clientName = String(trip?.guestName || trip?.clientName || '')
        .replace(/[^a-zA-Z0-9-_]/g, '-')
        .slice(0, 30);

      const filename = clientName
        ? `${safeRef}-${clientName}-Vouchers.pdf`
        : `${safeRef}-Bundle-Vouchers.pdf`;

      await generatePDF({ pageUrl: bundleUrl, filename });
      toast.success('Voucher bundle downloaded');
    } catch (err) {
      console.error('Bundle PDF failed:', err);
      toast.error('Bundle generation failed. Please try again.');
    } finally {
      setBundling(false);
    }
  };

  /* OLD BUNDLE RENDERER — html2canvas
     Replaced by Playwright Phase 7 (single-page render via /bundle-preview/:tripId).
     Kept commented out for emergency rollback only.

  const handleBundleExport_LEGACY = async () => {
    if (!bundleRef.current || vouchers.length === 0) return;
    setBundling(true);
    try {
      bundleRef.current.style.display = 'block';
      // Give the browser one paint frame so html2canvas sees the content
      await new Promise((resolve) => setTimeout(resolve, 100));
      const html2pdf = (await import('html2pdf.js')).default;
      const fname = trip ? `${trip.tripId}_vouchers_bundle.pdf` : 'vouchers_bundle.pdf';
      await html2pdf().set({
        margin: [8, 8, 8, 8],
        filename: fname,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'], before: '.voucher-page-break' },
      }).from(bundleRef.current).save();
      toast.success('Voucher bundle downloaded');
    } catch (err) {
      toast.error('Failed to generate bundle PDF');
    } finally {
      if (bundleRef.current) bundleRef.current.style.display = 'none';
      setBundling(false);
    }
  };
  */

  const handleCreate = (type) => { setCreatingType(type); setEditingVoucher(null); setView('form'); };
  const handleEdit = (voucher) => { setEditingVoucher(voucher); setCreatingType(voucher.type); setView('form'); };
  const handlePreview = (voucher) => { setPreviewVoucher(voucher); setView('preview'); };
  const handleDelete = async (voucherId) => {
    if (!window.confirm('Delete this voucher?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/vouchers/${voucherId}`);
      toast.success('Voucher deleted');
      fetchVouchers();
    } catch (err) { toast.error('Failed to delete'); }
  };
  const handleSaved = () => { setView('list'); fetchVouchers(); };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#2C3E35] border-t-transparent rounded-full animate-spin" /></div>;

  if (view === 'form') return <VoucherForm tripId={tripId} trip={trip} type={creatingType} editing={editingVoucher} onSaved={handleSaved} onCancel={() => setView('list')} />;
  if (view === 'preview') return <VoucherPreview voucher={previewVoucher} onBack={() => { setView('list'); fetchVouchers(); }} />;

  return (
    <div className="space-y-6" data-testid="vouchers-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {tripId && (
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="vouchers-back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Travel Vouchers
            </h1>
            {trip && <p className="text-sm text-slate-500 mt-1">{trip.guestName} — {trip.destination} · {trip.displayTripId || trip.tripId}</p>}
            {!tripId && <p className="text-sm text-slate-500 mt-1">All vouchers across trips</p>}
          </div>
        </div>
        {tripId && vouchers.length > 0 && (
          <Button
            onClick={handleBundleExport}
            disabled={bundling}
            className="bg-[#2C3E35] hover:bg-[#1E2B25] text-white rounded-sm"
            data-testid="bundle-export-btn"
          >
            {bundling
              ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating…</>
              : <><FileDown className="w-4 h-4 mr-2" /> Download Bundle PDF</>}
          </Button>
        )}
      </div>

      {/* Create New Voucher Buttons */}
      <div className="flex flex-wrap gap-3 items-center" data-testid="voucher-type-buttons">
        {tripId && Object.entries(VOUCHER_LABELS).map(([type, label]) => {
          const Icon = VOUCHER_ICONS[type];
          return (
            <Button key={type} variant="outline" onClick={() => handleCreate(type)} data-testid={`create-${type}-voucher-btn`}
              className="border-[#2C3E35]/20 text-[#2C3E35] hover:bg-[#2C3E35] hover:text-white transition-all rounded-sm">
              <Plus className="h-3.5 w-3.5 mr-1.5" /><Icon className="h-3.5 w-3.5 mr-1.5" />{label}
            </Button>
          );
        })}
        <Button
          onClick={() => setUploadOpen(true)}
          className="ml-auto bg-teal-600 hover:bg-teal-700 text-white"
          data-testid="upload-rebrand-btn"
        >
          <UploadCloud className="h-3.5 w-3.5 mr-1.5" />
          Upload &amp; Rebrand
        </Button>
      </div>

      {/* Voucher List */}
      {vouchers.length === 0 ? (
        <Card className="p-16 text-center border-dashed border-2 border-slate-200">
          <CalendarDays className="h-14 w-14 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">No vouchers created yet</p>
          {tripId && <p className="text-sm text-slate-400 mt-1">Create a voucher using the buttons above</p>}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vouchers.map(v => <VoucherCard key={v.voucherId} voucher={v} onPreview={handlePreview} onEdit={handleEdit} onDelete={handleDelete} />)}
        </div>
      )}

      {/* Hidden bundle renderer — only populated at PDF-export time */}
      {/* OLD html2canvas BUNDLE DOM — replaced by Playwright Phase 7
          (single-page render via /bundle-preview/:tripId).
          Kept commented out for emergency rollback only.

      <div ref={bundleRef} style={{ display: 'none', position: 'absolute', left: '-99999px' }} data-testid="voucher-bundle-root">
        {vouchers.map((v, idx) => (
          <BundleVoucherPage key={v.voucherId} voucher={v} company={company} isFirst={idx === 0} />
        ))}
      </div>
      */}

      <UploadRebrandDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        tripId={tripId}
        onCreated={() => fetchVouchers()}
      />
    </div>
  );
};

const BundleVoucherPage = ({ voucher, company, isFirst }) => {
  const qrValue = voucher.publicUrl || `${window.location.origin}/voucher/${voucher.voucherId}`;
  return (
    <div
      className={isFirst ? '' : 'voucher-page-break'}
      style={{ pageBreakBefore: isFirst ? 'auto' : 'always', pageBreakInside: 'avoid' }}
    >
      <BrandedVoucherLayout voucher={voucher} company={company} qrValue={qrValue} forPdf>
        {voucher.type === 'flight' && <FlightVoucherBody voucher={voucher} company={company} />}
        {voucher.type === 'hotel' && <HotelVoucherBody voucher={voucher} company={company} />}
        {voucher.type === 'activity' && <ActivityVoucherBody voucher={voucher} company={company} />}
        {voucher.type === 'transfer' && <TransferVoucherBody voucher={voucher} company={company} />}
        {voucher.type === 'itinerary' && <ItineraryVoucherBody voucher={voucher} company={company} />}
      </BrandedVoucherLayout>
    </div>
  );
};

const VoucherCard = ({ voucher, onPreview, onEdit, onDelete }) => {
  const Icon = VOUCHER_ICONS[voucher.type] || CalendarDays;
  const label = VOUCHER_LABELS[voucher.type] || voucher.type;
  const statusStyle = STATUS_STYLES[voucher.status] || STATUS_STYLES.draft;

  const getTitle = () => {
    if (voucher.type === 'flight') return voucher.flight?.airline ? `${voucher.flight.airline} ${voucher.flight.flightNumber}` : 'Flight Voucher';
    if (voucher.type === 'hotel') return voucher.hotel?.hotelName || 'Hotel Voucher';
    if (voucher.type === 'activity') return voucher.activity?.activityName || 'Activity Voucher';
    if (voucher.type === 'transfer') return `${voucher.transfer?.pickup || ''} → ${voucher.transfer?.drop || ''}`;
    if (voucher.type === 'itinerary') return 'Itinerary Voucher';
    return 'Voucher';
  };

  return (
    <Card className="group overflow-hidden hover:shadow-lg transition-all duration-300 border border-slate-200/80" data-testid={`voucher-card-${voucher.voucherId}`}>
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#2C3E35]/10 flex items-center justify-center">
              <Icon className="h-5 w-5 text-[#2C3E35]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{getTitle()}</p>
              <p className="text-[11px] text-slate-400 font-mono">{voucher.trvRef || voucher.voucherId}</p>
            </div>
          </div>
          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${statusStyle}`}>{voucher.status}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
          <span className="bg-slate-100 px-2 py-0.5 rounded font-medium">{label}</span>
          {voucher.destination && <span>{voucher.destination}</span>}
          {voucher.guestName && <span>· {voucher.guestName}</span>}
        </div>
        <div className="flex gap-2 pt-2 border-t border-slate-100">
          <Button size="sm" variant="ghost" onClick={() => onPreview(voucher)} data-testid={`preview-voucher-${voucher.voucherId}`}
            className="text-[#2C3E35] text-xs h-8">View</Button>
          <Button size="sm" variant="ghost" onClick={() => onEdit(voucher)} data-testid={`edit-voucher-${voucher.voucherId}`}
            className="text-slate-500 text-xs h-8">Edit</Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(voucher.voucherId)} data-testid={`delete-voucher-${voucher.voucherId}`}
            className="text-red-400 text-xs h-8 ml-auto">Delete</Button>
        </div>
      </div>
    </Card>
  );
};

export default Vouchers;
